import React, { useState } from "react";

import { CompositeFilterDescriptor, FilterDescriptor, process, State } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { Grid, GridCellProps, GridColumn, GridDataStateChangeEvent, GridToolbar } from "@progress/kendo-react-grid";
import { GridRowDoubleClickEvent } from "@progress/kendo-react-grid/dist/npm/interfaces/events";
import { Checkbox, CheckboxChangeEvent } from "@progress/kendo-react-inputs";
import find from "lodash/find";
import remove from "lodash/remove";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Dispatch } from "redux";

import { derender, render } from "../../../../../../state/actions/renderActions";
import { IDossierPrintData, IDossierTask } from "../../../../../../utils/types/models";
import { newKey } from "../../../../../../utils/utils";
import Confirm from "../../../../../global/confirm";
import { getGridCommandColumn } from "../../../../../global/gridpanel";
import { checkBoxCell, customCell, dateCell, multilineCell, translatedCell } from "../../../../../global/gridpanel/customCells/gridCells";
import { IFunctionCommand, IRecordCommand } from "../../../../../global/gridpanel/customCells/gridCells/commandCell";
import PrintDossierTask from "../../dossierTaskPrintView";

interface IProps {
	tasks: IDossierTask[];
	dossierPrintData?: IDossierPrintData
	showEditor?: (record?: IDossierTask) => void;
	onChange?: (tasks: IDossierTask[]) => void;
	readOnly?: boolean;
}

const TasksGrid: React.FC<IProps> = (props: IProps) => {
	const { t } = useTranslation();
	const [gridState, setGridState] = useState<State>({ sort: [{ field: "createdDate", dir: "desc" }] });
	const dispatch: Dispatch = useDispatch();

	function onDataStateChange(event: GridDataStateChangeEvent): void {
		setGridState(event.dataState);
	}

	function onRowDoubleClick(event: GridRowDoubleClickEvent): void {
		if (props.showEditor) {
			props.showEditor(event.dataItem);
		}
	}

	function getTaskCellClassName(gridCellProps: GridCellProps): string {
		const className: string = gridCellProps.dataItem.completed ? "green" : "";
		return className;
	}

	function showDossierTaskPrintPage(record: IDossierTask) {
		const key: string = newKey("Print_Page");
		dispatch(
			render(key, PrintDossierTask, {
				record,
				onClose: () => {
					dispatch(derender(key));
				},
				dossierPrintData: props.dossierPrintData,
				readOnly: props.readOnly,
			})
		);

	}

	let commands: (IFunctionCommand<IDossierTask> | IRecordCommand<IDossierTask>)[];
	if (props.showEditor && !props.readOnly) {
		commands = [
			{
				tooltip: t("edit"),
				iconClass: "las la-pencil-alt",
				recordAction: (record: IDossierTask) => {
					props.showEditor(record);
				}
			},
			{
				tooltip: t("remove"),
				iconClass: "las la-times",
				idAction: (id: number) => {
					const confirmKey: string = newKey("confirm");
					dispatch(
						render(confirmKey, Confirm, {
							title: t("confirm_title", { action: t("remove").toLowerCase() }),
							children: t("confirm_content", { action: t("remove").toLowerCase() }),
							onDecline: () => dispatch(derender(confirmKey)),
							onConfirm: () => {
								dispatch(derender(confirmKey));
								const newTasks: IDossierTask[] = [...props.tasks];
								remove(newTasks, { id });
								props.onChange(newTasks);
							}
						})
					);
				}
			},
			{
				tooltip: t("print"),
				iconClass: "las la-print",
				recordAction: showDossierTaskPrintPage
			}
		];
	}

	return (
		<Grid data={process(props.tasks, gridState)} {...gridState} sortable onDataStateChange={onDataStateChange} onRowDoubleClick={onRowDoubleClick} className="h-100">
			<GridToolbar>
				<div className={(props.showEditor && !props.readOnly ? "" : "noButtons ") + "toolbarButtonContainer d-flex width-100 align-items-center"}>
					{props.showEditor && !props.readOnly && (
						<Button primary onClick={() => props.showEditor()}>
							{t("add")}
						</Button>
					)}
					<div className="flex-grow-1" />
					<Checkbox
						onChange={(event: CheckboxChangeEvent) => {
							const newGridstate: State = { ...gridState };
							if (!newGridstate.filter) {
								newGridstate.filter = {
									logic: "and",
									filters: []
								};
							}

							const filter: CompositeFilterDescriptor | FilterDescriptor = find(newGridstate.filter.filters, { field: "completed" });
							if (!event.value && !filter) {
								newGridstate.filter.filters.push({ field: "completed", value: event.value, operator: "eq" });
							} else if (event.value && filter) {
								remove(newGridstate.filter.filters, { field: "completed" });
							}
							setGridState(newGridstate);
						}}
						label={t("showCompletedTasks")}
					/>
				</div>
			</GridToolbar>
			<GridColumn field="planned" title={t("planned")} width="80px" cell={customCell(checkBoxCell(false))} />
			<GridColumn field="createdDate" title={t("createdOn")} cell={customCell(dateCell())} width="140px" />
			<GridColumn field="createdBy" title={t("createdBy")} />
			<GridColumn field="type" title={t("type")} width="120px" cell={customCell(translatedCell())} />
			<GridColumn field="assignee1.fullName" title={t("lead")} width="160px" />
			<GridColumn field="startDate" title={t("startDate")} cell={customCell(dateCell(), getTaskCellClassName)} width="160px" />
			<GridColumn field="dateIsFixed" title={t("fixedDate")} width="100px" cell={customCell(checkBoxCell(false))} />
			<GridColumn field="description" title={t("task")} cell={customCell(multilineCell())} />
			{commands && commands.length > 0 && getGridCommandColumn(t("actions"), commands)}
		</Grid>
	);
};

export default TasksGrid;
